import type { IntegrationProvider } from '@/types';

export type TestEndpointConfig = {
  endpoint: 'testConn' | 'transformer' | 'sfAgent';
  envId?: string;
};

/**
 * Determines the API endpoint and URL format for test connection.
 * Matches dataProtect environmentActions.js tryCheckConnection() exactly:
 *
 * - TRANS_API_URL + LingkWebSocket → transformer with envId
 * - TEST_CONN_API_URL → test-connections service
 * - SF_AGENT_API_URL → SF agent service
 * - Ethos → transformer with envId
 * - default → transformer
 */
export const getTestEndpointConfig = (
  providerConfig: IntegrationProvider,
  envId: string
): TestEndpointConfig | null => {
  if (providerConfig.skipCheckConnection) {
    return null;
  }

  if (providerConfig.metadataApi === 'TRANS_API_URL' && providerConfig.type === 'LingkWebSocket') {
    return { endpoint: 'transformer', envId };
  }
  if (providerConfig.metadataApi === 'TEST_CONN_API_URL') {
    return { endpoint: 'testConn' };
  }
  if (providerConfig.metadataApi === 'SF_AGENT_API_URL') {
    return { endpoint: 'sfAgent' };
  }
  if (providerConfig.credentialType === 'Ethos') {
    return { endpoint: 'transformer', envId };
  }
  return { endpoint: 'transformer' };
};

/**
 * Deduplicate integration providers by label, handling legacy database entries
 *
 * Some providers have duplicate entries in the database with the same label
 * but different IDs. This function filters out duplicates, keeping only the
 * first occurrence of each unique label/type combination.
 *
 * @param providers - Array of integration providers
 * @returns Deduplicated array of providers
 *
 * @example
 * const providers = await fetchProviders();
 * const uniqueProviders = deduplicateProviders(providers);
 */
export const deduplicateProviders = (providers: IntegrationProvider[]): IntegrationProvider[] => {
  const seen = new Set<string>();

  return providers.filter((provider) => {
    // Use label as primary key, fallback to type
    const key = provider.label || provider.type;

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

/**
 * Sort providers alphabetically by label or type
 *
 * @param providers - Array of integration providers
 * @returns Sorted array of providers
 */
export const sortProviders = (providers: IntegrationProvider[]): IntegrationProvider[] => {
  return [...providers].sort((a, b) => {
    const aKey = (a.label || a.type || '').toLowerCase();
    const bKey = (b.label || b.type || '').toLowerCase();
    return aKey.localeCompare(bKey);
  });
};

/**
 * Deduplicate and sort providers in one operation
 *
 * @param providers - Array of integration providers
 * @returns Deduplicated and sorted array of providers
 */
export const normalizeProviders = (providers: IntegrationProvider[]): IntegrationProvider[] => {
  return sortProviders(deduplicateProviders(providers));
};
